"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

import Sites from "../../constants/Sites";
import NextImage from "@components/Image/NextImage";
import {
  clearCookie,
  findSiteNameWithoutCom,
  isIPadProOnSafari,
  slugRegexMatcher,
} from "helpers/Helper";
import { useTabletDetection } from "helpers/useTabletDetection";
import HeaderActionButtons from "@components/HeaderPage/HeaderActionButtons";
import { useAppContext } from "@components/Provider";
import PrivacyCookiePopup from "./PrivacyCookiePopup";
import OffCanvasMenu from "./OffCanvasMenu";
import AlgoliaSearch from "@components/AlgoliaSearch/AlgoliaSearch";

const NewsletterV2 = dynamic(() => import("@components/NewsletterV2/NewsletterV2"), { ssr: false });

import styles from "./NewHeader.module.css";
import "./NewHeader.css";

const NewHeader = (props) => {
  const {
    data,
    siteId,
    tokenCookie,
    subscriberCookie,
    isMobile,
    isTablet: serverIsTablet,
    hideEBBNl,
    ...appProps
  } = useAppContext();

  // Extract hideHeader prop
  let hideHeader = props?.hideHeader || false;

  // Enhanced client-side tablet detection using custom hook
  const { isTablet, isLargeTablet } = useTabletDetection(serverIsTablet, isMobile);

  const [privacyCookie, setPrivacyCookie] = useState("hidden");

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const value = localStorage.getItem("cookiePopup");
    if (value) {
      setPrivacyCookie("shown");
    }
  }, []);

  let siteName = data.SiteSettings.site_name;
  let slug = appProps.router.asPath && appProps.router.asPath.toString().replace(/%20/g, " ");

  let logoHeight = 99;

  const getSiteLogo = () => {
    let siteLogo;
    if (!isMobile && !isLargeTablet && slug === "/") {
      siteLogo = data.SiteSettings.primary_site_logo_svg_black;
      logoHeight = 60;
    } else if (isMobile || (isTablet && !isLargeTablet)) {
      siteLogo =
        data.SiteSettings.primary_site_logo_svg_black ||
        data.SiteSettings.primary_site_logo_svg ||
        data.SiteSettings.primary_site_logo;
      logoHeight = 30;
    } else {
      siteLogo =
        data.SiteSettings.primary_site_logo_svg_black ||
        data.SiteSettings.primary_site_logo_svg ||
        data.SiteSettings.primary_site_logo;
      logoHeight = 40;
    }
    return siteLogo;
  };

  //we need to render black ebb logo only on the desktop for the homepage as per the design guidelines
  const siteLogo = getSiteLogo();

  const handleLogout = () => {
    clearCookie("ormtoken", tokenCookie);
    clearCookie("subscribed");
    window.location.href = `/`;
  };

  let primaryNewsletter =
    siteId === Sites.OPENROADMEDIA
      ? findSiteNameWithoutCom(Sites.EARLYBIRDBOOKS)
      : findSiteNameWithoutCom(siteId);
  let headerNewsletterProps = {
    ...props,
    // optInNewsletter: {
    //   optInNewsletterCopy
    // },
    primaryNewsletter: primaryNewsletter,
  };

  const renderHeaderNL = () => {
    let NL;

    //EBB reverted to old design per https://openroadmedia.atlassian.net/browse/ORMTECH-952
    let newsletterLink = siteId == Sites.EARLYBIRDBOOKS ? "/newsletters-2" : "/newsletters";
    // if slug regex matches signup, dont display NL for mobile or desktop
    if (slugRegexMatcher(slug, "signup")) {
      return null;
    }
    if (isMobile || isTablet) {
      // Special case: For tablets on OPENROADMEDIA, show subscribe button (not newsletter component)
      if (isTablet && siteId === Sites.OPENROADMEDIA) {
        // Check if there's a cookie or if slug matches NL
        if (
          subscriberCookie ||
          slugRegexMatcher(slug, "newsletters") ||
          slugRegexMatcher(slug, "newsletters-2")
        ) {
          NL = null;
        } else {
          NL = (
            <div className="subscribeNL">
              <a href={newsletterLink}>Subscribe</a>
            </div>
          );
        }
      } else {
        // Original logic for mobile and other tablets
        if (isOpen) {
          NL = (
            <div className="subscribeMenu" onClick={() => setIsOpen(false)}>
              <a href={newsletterLink}>Subscribe</a>
            </div>
          );
        }
        // if its mobile/tablet and theres a cookie or if slug matches NL, dont display
        else if (
          subscriberCookie ||
          slugRegexMatcher(slug, "newsletters") ||
          slugRegexMatcher(slug, "newsletters-2")
        ) {
          NL = null;
        } else {
          NL = (
            <div className="subscribeNL">
              <NewsletterV2
                isChild={true}
                Newsletter_Settings={data.SiteSettings.newsletter_settings}
                classNames={"headerNewsletter"}
                {...headerNewsletterProps}
                siteId={siteId}
                inlineStyles={{ button: { backgroundColor: "#244090" } }}
              />
            </div>
          );
        }
      }
    } else {
      // Desktop logic - show subscribe button
      if (
        subscriberCookie ||
        slugRegexMatcher(slug, "newsletters") ||
        slugRegexMatcher(slug, "newsletters-2")
      ) {
        NL = null;
      } else {
        NL = !hideHeader && (
          <div className="subscribeNL">
            <a href={newsletterLink}>Subscribe</a>
          </div>
        );
      }
    }
    return NL;
  };
  let style = {};
  if (slug === "/" && !isMobile && !isLargeTablet) {
    style = {
      background: "#fff",
      color: "#000",
      // borderBottom: "1px solid #000",
      // marginBottom: "4rem",
    };
  } else {
    if (isMobile || (isTablet && !isLargeTablet)) {
      style = {
        background: "var(--headerBackgroundColor, #fff)",
        color: "var(--headerTextColor, #000)",
        // borderBottom: "1px solid var(--newOneBorderColor, #000)",
        // marginBottom: "var(--newHeaderMarginBottom, 0)",
      };
    }
  }
  style["--headerTextColor"] = "#000";
  style["--newOneBorderColor"] = "#000";
  style["--algSearchVisible"] = 1;
  style["--newHeaderPadding"] = props.hideMenuItems ? "0.5rem" : "4px 0";
  style["--menuColor"] = "#000";
  style["--logoMargin"] = props.hideMenuItems ? "auto" : "0";
  style["--headerRightMarginLeft"] =
    isMobile || (isTablet && !isLargeTablet) || slug !== "/" ? "auto" : "0";
  style["--m576newHeaderPadding"] = props.hideMenuItems ? "0.3rem" : "4px 12px";
  style["--m576logoPosition"] = "static";
  style["--m576logoLeft"] = "0";
  style["--m576logoOpacity"] = "1";

  // Add styles for hiding search bar and centering logo
  if (props.hideSearchBar) {
    style["--logoCenter"] = "center";
    style["--searchBarDisplay"] = "none";
    style["--menuJustifyContent"] = "center";
  }

  return (
    <>
      {privacyCookie !== "shown" && (
        <PrivacyCookiePopup
          setPrivacyCookie={setPrivacyCookie}
          siteId={siteId}
          data={data}
          isFeedbackPage={slugRegexMatcher(slug, "feedback")}
          isMobile={isMobile}
        />
      )}
      <header
        className={[
          styles["new-header"],
          props.hideMenuItems && styles["new-header-margin-bottom"],
          "new-header",
        ].join(" ")}
        style={{ ...style, paddingLeft: 0, paddingRight: 0 }}
      >
        <ul
          className={[
            styles["menu"],
            !isMobile &&
              !isTablet &&
              slug === "/" &&
              !props.hideSearchBar &&
              styles["menuJustifyContent"],
            props.hideSearchBar && styles["menuCenterLogo"],
          ].join(" ")}
          style={{
            ...(isMobile || isTablet ? { borderBottom: "1px solid #000" } : {}),
            margin: !isTablet && !isMobile ? "0 3rem" : "",
            padding: isTablet || isMobile ? "0.5rem 1rem" : "",
            justifyContent: props.hideSearchBar ? "center" : undefined,
          }}
        >
          {/* {!props?.hideMenuItems && (isMobile || isTablet) && (
            <li>
              <OffCanvasMenu handleLogout={handleLogout} slug={slug} activeBottomNavIndex={props.activeBottomNavIndex} />
            </li>
          )} */}
          {!props?.hideMenuItems && (isMobile || isTablet) ? (
            <li style={{ display: "flex", alignItems: "center" }}>
              <OffCanvasMenu
                handleLogout={handleLogout}
                slug={slug}
                activeBottomNavIndex={props.activeBottomNavIndex}
                onMenuClose={props.onMenuClose}
              />
              <a href="/" style={{ marginLeft: isTablet ? 12 : "1rem" }}>
                <NextImage
                  ImageUrl={siteLogo}
                  Title={siteName}
                  Alt_Text={siteName}
                  style={{ display: "block", margin: "0 auto" }}
                  width={250}
                  height={logoHeight}
                  isLazyload={false}
                  fetchpriority={true}
                />
              </a>
            </li>
          ) : (
            <li
              className={[
                styles["logo"],
                props.hideMenuItems && styles["logoHideMenu"],
                props.hideSearchBar && styles["logoCentered"],
              ].join(" ")}
            >
              <a href="/">
                <NextImage
                  ImageUrl={siteLogo}
                  Title={siteName}
                  Alt_Text={siteName}
                  style={{ display: "block", margin: "0 auto" }}
                  width={250}
                  height={logoHeight}
                  isLazyload={false}
                  fetchpriority={true}
                />
              </a>
            </li>
          )}
          {!hideHeader && !isMobile && !isTablet && !props.hideSearchBar && (
            <li className={styles.searchBarRow}>
              <AlgoliaSearch />
            </li>
          )}
          {!hideHeader && !props?.hideMenuItems && !props.hideSearchBar && (
            <div
              className={styles["header-right"]}
              style={{
                display: "flex",
                alignItems: "center",
                marginLeft: "auto",
                visibility: "visible",
                opacity: 1,
              }}
            >
              {!isMobile && !isTablet && !isIPadProOnSafari() && (
                <HeaderActionButtons slug={slug} />
              )}
              {/* Show action buttons for tablets */}
              {isTablet && !tokenCookie ? <HeaderActionButtons slug={slug} /> : null}
              {isTablet && tokenCookie ? (
                <li className={styles["tablet-account"]}>
                  <a
                    href="/account/profile"
                    className={styles.offcanvasAccountLink}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontWeight: "bold",
                      fontSize: 16,
                      textDecoration: "none",
                      color: "#222",
                    }}
                  >
                    <img
                      src={`${process.env.S3_IMAGE_URL}/Vector.svg`}
                      alt="My Account Icon"
                      className={styles.offcanvasAccountIcon}
                      style={{
                        width: 22,
                        height: 22,
                        marginRight: "10px",
                        verticalAlign: "middle",
                      }}
                    />
                    My Account
                  </a>
                  <button onClick={handleLogout} className={styles.logoutButton}>
                    Log Out
                  </button>
                </li>
              ) : null}
              {tokenCookie && !isTablet && !isMobile ? (
                <li className={styles.accountSection}>
                  <a
                    href="/account/profile"
                    className={styles.offcanvasAccountLink}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      textDecoration: "none",
                    }}
                  >
                    <img
                      src={`${process.env.S3_IMAGE_URL}/Vector.svg`}
                      alt="My Account Icon"
                      className={styles.offcanvasAccountIcon}
                    />
                    My Account
                  </a>
                  <button onClick={handleLogout} className={styles.logoutButton}>
                    Log Out
                  </button>
                </li>
              ) : null}
            </div>
          )}
        </ul>
        {/* hide newsletter from header on mobile and tablet, if nonclosablemodal is checked */}
        {isMobile && !hideEBBNl && !props.hideMenuItems && !props.isNonClosableModalChecked
          ? renderHeaderNL()
          : null}
        {/* hide newsletter from header on desktop and tablet, if nonclosablemodal is checked */}
        {!hideHeader &&
          !isMobile &&
          !isTablet &&
          !props.isNonClosableModalChecked &&
          renderHeaderNL()}
        {/* Show newsletter for tablet on OPENROADMEDIA */}
        {!hideHeader &&
        isTablet &&
        siteId === Sites.OPENROADMEDIA &&
        !props.isNonClosableModalChecked
          ? renderHeaderNL()
          : null}
      </header>
    </>
  );
};

export default NewHeader;
